<template>
  <Drawer
    v-model:visible="visible"
    position="right"
    :style="{ width: '28rem' }"
  >
    <template #header>
      <div>
        <h2 class="text-navy-800 text-base font-semibold">Edit Company</h2>
        <p class="text-[13px] text-neutral-500">Update company details</p>
      </div>
    </template>

    <div class="space-y-4">
      <div>
        <label class="text-navy-800 mb-1 block text-[13px] font-medium">
          Company Name
        </label>
        <input
          v-model="nameValue"
          type="text"
          placeholder="Company name"
          class="text-navy-800 placeholder-navy-300 w-full rounded-md border border-neutral-300 px-3 py-[9px] text-[13px] focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none"
          @keydown.enter="saveChanges"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3">
        <AppButton variant="secondary" size="sm" @click="visible = false">
          Cancel
        </AppButton>
        <AppButton
          :loading="saving"
          :disabled="!hasChanges"
          @click="saveChanges"
        >
          Save Changes
        </AppButton>
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import Drawer from "primevue/drawer"
import { useToast } from "primevue/usetoast"
import { computed, ref, watch } from "vue"

import type { CompanyType } from "../../pages/Company/types"
import { apolloClient } from "../../queries/client"
import UPDATE_COMPANY from "../../queries/company/update.gql"
import AppButton from "../ui/AppButton.vue"

interface Props {
  company: CompanyType
}

const props = defineProps<Props>()

const visible = defineModel<boolean>("visible", { default: false })

const emit = defineEmits<{
  updated: [company: { id: string; name: string }]
}>()

const toast = useToast()
const nameValue = ref("")
const saving = ref(false)

const hasChanges = computed(() => nameValue.value.trim() !== props.company.name)

watch(visible, (isOpen) => {
  if (isOpen) {
    nameValue.value = props.company.name
  }
})

const saveChanges = async () => {
  const trimmed = nameValue.value.trim()
  if (!trimmed || !hasChanges.value) return

  saving.value = true
  try {
    const result = await apolloClient.mutate({
      mutation: UPDATE_COMPANY,
      variables: {
        companyId: props.company.id,
        name: trimmed,
      },
    })
    const data = result.data?.updateCompany
    if (data?.errors?.length) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: data.errors.join(", "),
        life: 4000,
      })
      return
    }

    toast.add({
      severity: "success",
      summary: "Saved",
      detail: "Company updated",
      life: 3000,
    })

    emit("updated", { id: props.company.id, name: trimmed })
    visible.value = false
  } catch {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to update company",
      life: 4000,
    })
  } finally {
    saving.value = false
  }
}
</script>
